import { TimeSlot } from '../WorkingHours.types';
import { MINUTES_PER_DAY } from '../workingHoursConstants';

const pad2 = (value: number) => String(value).padStart(2, '0');

export const minutesToTime = (minutes: number) => {
  const normalized = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  return `${pad2(hours)}:${pad2(mins)}`;
};

// Display-only 12h formatter — do not use for <input type="time"> values (those need 24h HH:MM).
export const minutesToTime12h = (minutes: number) => {
  const normalized = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours24 = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const period = hours24 < 12 ? 'AM' : 'PM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${pad2(mins)} ${period}`;
};

// Reads startTime/endTime back into clock minutes. The backend has been observed
// returning this as either a plain "HH:mm:ss" string (matching what requests send) or a
// TimeSpan-shaped object ({ hours, minutes, ... }) — handle both defensively so a format
// change on either side doesn't silently produce NaN.
export const timeSpanToMinutes = (
  timeSpan: { hours: number; minutes: number } | string | null | undefined
): number => {
  if (!timeSpan) return 0;
  if (typeof timeSpan === 'string') {
    const [hoursStr, minutesStr] = timeSpan.split(':');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    return (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);
  }
  const hours = Number.isFinite(timeSpan.hours) ? timeSpan.hours : 0;
  const minutes = Number.isFinite(timeSpan.minutes) ? timeSpan.minutes : 0;
  return hours * 60 + minutes;
};

export const getSlotAlphaLabel = (index: number) => {
  if (index < 0) return 'A';

  let value = index;
  let result = '';

  while (value >= 0) {
    result = String.fromCharCode((value % 26) + 65) + result;
    value = Math.floor(value / 26) - 1;
  }

  return result;
};

export const isFullDaySlot = (slot: TimeSlot) =>
  slot.startMinutes === 0 && slot.endMinutes === MINUTES_PER_DAY;

export const getSlotChipLabel = (slot: TimeSlot, index: number) => {
  if (isFullDaySlot(slot)) return 'Full Day';

  const alphaLabel = getSlotAlphaLabel(index);
  return `${alphaLabel}: ${minutesToTime12h(slot.startMinutes)} - ${minutesToTime12h(
    slot.endMinutes
  )}`;
};

export const getSlotBarLabel = (slot: TimeSlot, index: number) => {
  if (isFullDaySlot(slot)) return 'Full Day';

  const alphaLabel = getSlotAlphaLabel(index);
  return `${alphaLabel}: ${minutesToTime12h(slot.startMinutes)} - ${minutesToTime12h(
    slot.endMinutes
  )}`;
};
